import { useState, useEffect, createContext, useContext } from 'react';
import { useMutation, useQueryClient } from 'react-query';
import { authApi } from '@/services/authApi';
import { User } from '@/types/user';

interface AuthContextType {
  user: User | null;
  isLoading: boolean;
  login: (email: string, password: string) => Promise<void>;
  register: (data: RegisterData) => Promise<void>;
  logout: () => void;
}

interface RegisterData {
  email: string;
  password: string;
  firstName: string;
  lastName: string;
  userType: 'STUDENT' | 'AGENT';
  phone?: string;
  agentCode?: string;
  agencyName?: string;
  licenseNumber?: string;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }: { children: React.ReactNode }) => {
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const queryClient = useQueryClient();

  // Check if user is logged in on app start
  useEffect(() => {
    const initializeAuth = async () => {
      const token = localStorage.getItem('token');
      console.log('Initializing auth, token exists:', !!token);
      
      if (token) {
        try {
          console.log('Fetching user profile...');
          // Verify token and get user data
          const userData = await authApi.getProfile();
          console.log('Profile fetched successfully:', { 
            id: userData.id, 
            email: userData.email, 
            userType: userData.userType 
          });
          
          // Only allow agents to access the agent portal
          if (userData.userType === 'AGENT') {
            console.log('User is an agent, setting user state');
            setUser(userData);
          } else {
            // Not an agent, clear tokens
            console.log('User is not an agent, clearing tokens');
            localStorage.removeItem('token');
            localStorage.removeItem('refreshToken');
            setUser(null);
          }
        } catch (error: any) {
          // Token is invalid or expired, clear it
          console.error('Failed to restore auth session:', {
            message: error.message,
            status: error.response?.status,
            data: error.response?.data
          });
          localStorage.removeItem('token');
          localStorage.removeItem('refreshToken');
          setUser(null);
        } finally {
          setIsLoading(false);
        }
      } else {
        // No token found
        console.log('No token found, setting loading to false');
        setIsLoading(false);
      }
    };

    initializeAuth();
  }, []);

  const loginMutation = useMutation(authApi.login, {
    onSuccess: async (data) => {
      console.log('Login mutation success:', data);
      // Only allow agents to access the agent portal
      if (data.user.userType === 'AGENT') {
        localStorage.setItem('token', data.token);
        if (data.refreshToken) {
          localStorage.setItem('refreshToken', data.refreshToken);
        }
        
        // Fetch full profile to ensure we have all latest data including permissions
        try {
          const fullProfile = await authApi.getProfile();
          console.log('Full profile fetched after login:', fullProfile);
          setUser(fullProfile);
        } catch (profileError) {
          console.error('Error fetching profile after login, using login response:', profileError);
          // Fallback to login response if profile fetch fails
          // Transform login response to match User type (handle snake_case to camelCase)
          const loginUser = data.user as any;
          const transformedUser: User = {
            ...loginUser,
            agentProfile: loginUser.agent_profiles || loginUser.agentProfile || null,
            studentProfile: loginUser.student_profiles || loginUser.studentProfile || null,
            adminProfile: loginUser.admin_profiles || loginUser.adminProfile || null,
          };
          // Remove snake_case properties if they exist
          delete (transformedUser as any).agent_profiles;
          delete (transformedUser as any).student_profiles;
          delete (transformedUser as any).admin_profiles;
          setUser(transformedUser);
        }
        
        queryClient.invalidateQueries();
      } else {
        throw new Error('Access denied. This portal is for agents only.');
      }
    },
    onError: (error) => {
      console.error('Login error:', error);
    },
  });

  const registerMutation = useMutation(authApi.register, {
    onSuccess: async (data) => {
      // Only allow agent registration
      if (data.user.userType === 'AGENT') {
        localStorage.setItem('token', data.token);
        if (data.refreshToken) {
          localStorage.setItem('refreshToken', data.refreshToken);
        }
        
        // Fetch full profile to ensure we have all latest data including permissions
        try {
          const fullProfile = await authApi.getProfile();
          setUser(fullProfile);
        } catch (profileError) {
          console.error('Error fetching profile after registration, using registration response:', profileError);
          // Fallback to registration response if profile fetch fails
          const regUser = data.user as any;
          const transformedUser: User = {
            ...regUser,
            agentProfile: regUser.agent_profiles || regUser.agentProfile || null,
            studentProfile: regUser.student_profiles || regUser.studentProfile || null,
            adminProfile: regUser.admin_profiles || regUser.adminProfile || null,
          };
          delete (transformedUser as any).agent_profiles;
          delete (transformedUser as any).student_profiles;
          delete (transformedUser as any).admin_profiles;
          setUser(transformedUser);
        }
        
        queryClient.invalidateQueries();
      } else {
        throw new Error('Invalid user type for agent portal');
      }
    },
    onError: (error) => {
      console.error('Registration error:', error);
    },
  });

  const login = async (email: string, password: string) => {
    await loginMutation.mutateAsync({ email, password });
  };

  const register = async (data: RegisterData) => {
    await registerMutation.mutateAsync(data);
  };

  const logout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('refreshToken');
    setUser(null);
    queryClient.clear();
  };

  const value = {
    user,
    isLoading,
    login,
    register,
    logout,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};
